import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ReactDOM from 'react-dom';

import { getRulesFromState, get, interpolateString } from 'ctm-util-functions';

import { Notification } from '../../notification/notification';
import { InternalLink } from '../../internal_link/internal_link';

const _FormGroupNotification = (props) => {
    const placement = 'bottom';
    const variant = 'variant_grey';
    const visualProps = { variant, placement };
    const { notificationProps, notificationText } = props;

    /**
     * Returns true if the notification should be visible, based on props and optional rules
     * @returns {Boolean} - True if the notification should be shown, false otherwise
     */
    const isVisible = () => {
        if (
            !notificationProps
            || !notificationText
            || !notificationText.content
        ) {
            return false;
        }
        return get(props, 'rules.visibility.result', true);
    };

    const getContent = () => {
        let content = interpolateString(props.notificationText.content)(props.selectedValues);
        const { internalLink } = props.notificationText;

        if (!content) {
            return null;
        }

        if (content.includes('{internalLink}')) {
            const link = <InternalLink {...internalLink} className={internalLink.className} />;
            const [beforeLink, afterLink] = content.split('{internalLink}');
            content = (
                <span>
                    {beforeLink}
                    {link}
                    {afterLink}
                </span>
            );
        }
        return content;
    };

    const hasContentGotHtml = () => {
        const { convertContentStrToHtmlNode } = props.notificationText;
        return convertContentStrToHtmlNode;
    };

    if (!isVisible()) {
        return null;
    }

    const content = getContent();

    return (
        <>
            {content
                && (
                    <Notification
                        elementProps={notificationProps}
                        content={content}
                        visualProps={visualProps}
                        convertContentStrToHtmlNode={hasContentGotHtml()}
                        icon
                    />
                )}
        </>
    );
};

_FormGroupNotification.propTypes = {
    notificationText: PropTypes.object,
    notificationProps: PropTypes.object,
    // eslint-disable-next-line react/no-unused-prop-types
    rules: PropTypes.object,
    selectedValues: PropTypes.object
};

/**
 * Add processed rules to props if rules config is present
 * @param {Object} state - The app state
 * @param {Object} props - The component props
 * @returns {Object} - The mapped props
 */
const mapStateToProps = (state, props) => {
    let rules = {};
    if (get(props, 'notificationText.rules')) {
        rules = getRulesFromState(state, { rules: props.notificationText.rules });
    }
    return { rules };
};

/**
 * onClick Event handler to be passed as callback.
 * Set the size and position relative to the viewport of the notification parent and trigger elements
 * using the getBoundingClientRect() method to get an ClientRect object with those properties: left, top, right, bottom, width, height
 * I tried forcing calling the 'toggleNotification' callback passing a fake event from RadioButtonGroup
 * with ReactDOM.findDOMNode(this) as target but the resulting position relative to the viewport value was not the expected.
 *
 * @param {Object} event - the event details.
 * @param {Node} formGroup - The formGroup component that the notification is being toggled for
 * @returns {Object} - The resulting notificationProps
 *
 */
const toggleNotification = (event, formGroup) => {
    // Get the size and position relative to the viewport of the corresponding native DOM element to the RadioButtonContainer component.
    const parentRect = ReactDOM.findDOMNode(formGroup).getBoundingClientRect(); // eslint-disable-line react/no-find-dom-node
    // Get the size of the trigger element and its position relative to the viewport.
    const triggerRect = event.target.getBoundingClientRect();
    // Set the details to the notification props.
    return { notificationProps: { parentRect, triggerRect } };
};

const FormGroupNotification = connect(mapStateToProps)(_FormGroupNotification);

export { FormGroupNotification, toggleNotification, _FormGroupNotification };
